import {
  AirlinesTwoTone,
  CalendarMonthTwoTone,
  FlightLandTwoTone,
  FlightTakeoffTwoTone,
  PeopleTwoTone,
  PrintTwoTone,
  ShareTwoTone,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Hidden,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  useTheme,
  MenuItem,
  Menu,
  IconButton,
  Stack,
  Badge,
  SxProps,
  Dialog,
  DialogContent,
  Tooltip,
} from '@mui/material';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkBehavior } from './LinkBehavior';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCarousel from './PhotoCarousel';
import PhotoCarouselDesc from './PhotoCarouselDesc';
import { CallBackFormDialog } from './CallBackFormDialog';
import { sendToNative } from '../utils';

type OnlineTourCardProps = {
  tourId: number;
  tourImage?: { path: string; videoPath: string; favorite: boolean }[] | null;
  coverImg: string | null;
  tourName: string;
  tourCode: string;
  start: {
    departureCity: string;
    departureAirport?: string;
    arrivalCity: string;
    arrivalAirport?: string;
  };
  end: {
    departureCity: string;
    departureAirport?: string;
    arrivalCity: string;
    arrivalAirport?: string;
  };
  tourStartDate: string;
  tourFinishDate: string;
  night: number;
  day: number;
  tourPrice?: number;
  currencyCode: string;
  flightCompanyInfo?: {
    logo: string;
    name: string;
  };
  remainingTime: number | null;
  remainingSeat: number | null;
  capacity?: number;
  disableActions?: boolean;
  hideShareAndPrint?: boolean;
  showBookButton?: boolean;
  showCallMeButton?: boolean;
  hideCoverImage?: boolean;
  whatsappPhoneNumber?: string;
};

const Image = styled('img')``;

export function OnlineTourCard({
  tourId,
  tourImage,
  tourName,
  tourCode,
  start,
  end,
  tourStartDate,
  tourFinishDate,
  night,
  day,
  tourPrice,
  currencyCode,
  flightCompanyInfo,
  remainingTime,
  remainingSeat,
  capacity,
  coverImg,
  disableActions,
  hideShareAndPrint,
  showBookButton,
  showCallMeButton,
  hideCoverImage,
  whatsappPhoneNumber,
}: OnlineTourCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const showSnackbar = useAlertSnackbar();
  const [tourUrl, setTourUrl] = useState<string>('');
  const location = useLocation();
  const [isCarouselOpen, setCarouselOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpenCarousel = () => {
    setCarouselOpen(true);
  };

  const handleCloseCarousel = () => {
    setCarouselOpen(false);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleShare = (
    event: React.MouseEvent<HTMLElement>,
    tourId: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setTourUrl(`https://hisarturizm.com.tr/turlar/?path=%2Ftours%2F${tourId}`);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(tourUrl);
    showSnackbar(t('guideCustomerOperationState.copied'), 'success');
    handleCloseMenu();
  };

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(tourUrl)}`;
    window.open(whatsappUrl, '_blank');
    handleCloseMenu();
  };

  const formatDateWithoutYear = (date?: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
    };

    if (date) return new Date(date).toLocaleDateString('tr-TR', dateOptions);
    return '-';
  };
  const formatDate = (date?: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };

    if (date) return new Date(date).toLocaleDateString('tr-TR', dateOptions);
    return '-';
  };

  const handlePrint = () => {
    const currentPath = location.pathname;

    if (currentPath.includes('/tours/')) {
      window.open(
        `/tour-print-out/${tourId}`,
        '_blank',
        'width=1200,height=1000,scrollbars=yes,resizable=yes',
      );
    } else if (currentPath.includes('/tour-print-out/')) {
      if (window.Android) {
        sendToNative('print', null);
      } else {
        window.print();
      }
    }
  };

  const message = `${t('onlineToursPage.getInfoWithWhatsAppMessage', {
    tourName: tourName,
    tourCode: tourCode,
  })}`;

  const shareAndPrint = hideShareAndPrint ? null : (
    <Box className="hide-from-print-section">
      <Tooltip placement="top" title={t('onlineToursPage.print')}>
        <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
          <PrintTwoTone />
        </IconButton>
      </Tooltip>
      <Tooltip placement="top" title={t('onlineToursPage.share')}>
        <IconButton onClick={(e) => handleShare(e, tourId)} sx={{ mr: 1 }}>
          <ShareTwoTone />
        </IconButton>
      </Tooltip>
      {whatsappPhoneNumber && (
        <Tooltip
          placement="top"
          title={t('onlineToursPage.getInfoWithWhatsAppMessageToolTip')}
        >
          <IconButton
            component="a"
            href={`https://api.whatsapp.com/send?phone=${whatsappPhoneNumber}&text=${encodeURIComponent(
              message,
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleCopyLink}>
          <ContentCopyIcon color="primary" />
          <Typography sx={{ ml: 1 }}>
            {t('guideCustomerOperationState.copyLink')}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleShareWhatsApp}>
          <WhatsAppIcon color="primary" />
          <Typography sx={{ ml: 1 }}>
            {t('guideCustomerOperationState.shareInWhatsApp')}
          </Typography>
        </MenuItem>{' '}
      </Menu>
    </Box>
  );

  return (
    <Card
      sx={(t) => ({
        [t.breakpoints.up('md')]: {
          display: 'flex',
          position: 'relative',
        },
      })}
    >
      {
        <CardMedia
          onClick={tourImage ? handleOpenCarousel : undefined}
          sx={{
            cursor: tourImage ? 'pointer' : 'default',
            position: 'relative',
            flex: hideCoverImage ? 0 : 1,
          }}
        >
          {!hideCoverImage && (
            <Image
              src={coverImg || 'https://placehold.co/600x600'}
              alt="cover"
              sx={{
                width: '100%',
                objectFit: 'cover',
                [theme.breakpoints.up('md')]: {
                  height: '100%',
                },
                [theme.breakpoints.down('md')]: {
                  height: '140px',
                },
              }}
            />
          )}
          {!hideCoverImage && (
            <TourCardChips
              remainingTime={remainingTime}
              remainingSeat={remainingSeat}
              sx={{
                position: 'absolute',
                p: 1,
                bottom: 0,
                left: 0,
              }}
            />
          )}
        </CardMedia>
      }
      <Box sx={{ flex: 3 }}>
        <CardContent
          sx={{
            [theme.breakpoints.up('md')]: {
              display: 'flex',
              justifyContent: 'space-between',
            },
            height: 'calc(100% - 40px)',
          }}
        >
          <Box sx={{ flex: 3 }}>
            <Typography gutterBottom variant="h5" component="h3" mb={0}>
              {tourName}
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="GrayText">
                {t('onlineToursPage.tourCode')} {tourCode}
              </Typography>
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {shareAndPrint}
              </Box>
            </Stack>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CalendarMonthTwoTone />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Fragment>
                      {formatDateWithoutYear(tourStartDate)} -{' '}
                      {formatDate(tourFinishDate)}
                    </Fragment>
                  }
                  secondary={
                    <Fragment>
                      {day === 1
                        ? t('onlineToursPage.oneDayTour')
                        : `${night} ${t('onlineToursPage.night')} - ${day} ${t(
                            'onlineToursPage.day',
                          )}`}
                    </Fragment>
                  }
                />
              </ListItem>
              {start.departureCity && start.arrivalCity && (
                <FlightListItem icon={<FlightTakeoffTwoTone />} {...start} />
              )}
              {end.departureCity && end.arrivalCity && (
                <FlightListItem icon={<FlightLandTwoTone />} {...end} />
              )}
              {flightCompanyInfo && (
                <ListItem>
                  <ListItemIcon>
                    <AirlinesTwoTone />
                  </ListItemIcon>
                  <ListItemText primary={flightCompanyInfo?.name} />
                </ListItem>
              )}
              {capacity && (
                <ListItem>
                  <ListItemIcon>
                    <PeopleTwoTone />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('onlineToursPage.capacityLabel')}
                    secondary={`${t('onlineToursPage.capacity', {
                      total: capacity,
                      registered: capacity - (remainingSeat || 0),
                    })}`}
                  />
                </ListItem>
              )}
            </List>
            {hideCoverImage && (
              <TourCardChips
                remainingTime={remainingTime}
                remainingSeat={remainingSeat}
              />
            )}
          </Box>
          {!tourPrice && disableActions && hideShareAndPrint ? null : (
            <Box
              sx={{
                flex: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                textAlign: 'right',
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {shareAndPrint}
              </Box>
              <div></div>
              {tourPrice && (
                <Box
                  sx={{
                    [theme.breakpoints.down('xs')]: {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    },
                  }}
                >
                  <Typography>
                    {t('onlineToursPage.perPersonInDoubleRoom')}
                  </Typography>

                  <Box>
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{ color: (t) => t.palette.primary.main }}
                    >
                      {tourPrice.toLocaleString('tr-TR', {
                        style: 'currency',
                        currency: currencyCode,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      [theme.breakpoints.down('sm')]: {
                        display: 'flex',
                        flexDirection: 'column',
                      },
                      gap: 2,
                      alignSelf: 'end',
                      justifyContent: 'end',
                    }}
                  >
                    {showBookButton && showCallMeButton && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        sx={{ mt: 1 }}
                        size="large"
                        onClick={() => setOpen(true)}
                        disabled={!isTourBookable(remainingTime, remainingSeat)}
                      >
                        {t('onlineToursPage.letUsCallYou')}
                      </Button>
                    )}
                    {showBookButton && (
                      <Badge
                        badgeContent={t('onlineToursPage.new')}
                        color="error"
                        overlap="circular"
                        sx={{
                          '.MuiBadge-badge': {
                            right: 24,
                          },
                        }}
                      >
                        <Button
                          href={`/tours/${tourId}/purchase`}
                          variant="contained"
                          LinkComponent={LinkBehavior}
                          sx={{ mt: 1, width: '100%' }}
                          size="large"
                          disabled={
                            !isTourBookable(remainingTime, remainingSeat)
                          }
                        >
                          {t('onlineToursPage.book')}
                        </Button>
                      </Badge>
                    )}
                  </Box>
                </Box>
              )}
              {!disableActions && (
                <Hidden mdDown>
                  <div>
                    <Button
                      href={`/tours/${tourId}`}
                      variant="contained"
                      LinkComponent={LinkBehavior}
                    >
                      {t('onlineToursPage.examine')}
                    </Button>
                  </div>
                </Hidden>
              )}
            </Box>
          )}
        </CardContent>

        {!disableActions && (
          <Hidden mdUp>
            <CardActions
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Button
                href={`/tours/${tourId}`}
                variant="contained"
                fullWidth
                LinkComponent={LinkBehavior}
              >
                {t('onlineToursPage.examine')}
              </Button>
            </CardActions>
          </Hidden>
        )}
      </Box>
      <CallBackFormDialog
        tourId={tourId}
        open={open}
        onClose={() => setOpen(false)}
      />
      <Dialog
        open={isCarouselOpen}
        onClose={handleCloseCarousel}
        maxWidth="md"
        fullWidth
      >
        <IconButton
          onClick={handleCloseCarousel}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            backgroundColor: 'white',
            color: 'black',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <PhotoCarousel itemName={tourName} images={tourImage || []} />
          {/*<PhotoCarouselDesc />*/}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function FlightListItem({
  icon,
  departureCity,
  departureAirport,
  arrivalCity,
  arrivalAirport,
}: {
  icon: React.ReactNode;
  departureCity: string;
  departureAirport?: string;
  arrivalCity: string;
  arrivalAirport?: string;
}) {
  return (
    <ListItem>
      <ListItemIcon>{icon}</ListItemIcon>
      <Box>
        <ListItemText
          primary={
            <span>
              {departureCity}{' '}
              {departureAirport && (
                <Typography color="GrayText" variant="inherit" component="span">
                  ({departureAirport})
                </Typography>
              )}
            </span>
          }
        />
        <ListItemText
          primary={
            <span>
              {arrivalCity}{' '}
              {arrivalAirport && (
                <Typography color="GrayText" variant="inherit" component="span">
                  ({arrivalAirport})
                </Typography>
              )}
            </span>
          }
        />
      </Box>
    </ListItem>
  );
}

function TourCardChips({
  remainingTime,
  remainingSeat,
  sx,
}: {
  remainingTime: number | null;
  remainingSeat: number | null;
  sx?: SxProps;
}) {
  const { t } = useTranslation();

  return (
    <Box sx={sx}>
      {remainingSeat !== null && (
        <Chip
          variant="filled"
          color={remainingSeat > 0 ? 'primary' : 'error'}
          label={
            remainingSeat > 0
              ? `${t('onlineToursPage.lastSeats', {
                  seats: remainingSeat,
                })}`
              : `${t('onlineToursPage.noCapacity')}`
          }
          sx={{ mr: 1, color: (t) => t.palette.common.white }}
        />
      )}
      {(remainingSeat === null || remainingSeat > 0) &&
        (remainingTime && remainingTime > 0 ? (
          <Chip
            variant="filled"
            color="info"
            label={t('onlineToursPage.lastNDaysToRegister', {
              remainingTime,
            })}
          />
        ) : (
          remainingTime !== null && (
            <Chip
              variant="filled"
              color="warning"
              label={t('onlineToursPage.registerTimeExpired')}
              sx={{ mr: 1 }}
            />
          )
        ))}
    </Box>
  );
}

export function isTourBookable(
  remainingTime: number | null,
  remainingSeat: number | null,
) {
  if (remainingSeat === null || remainingSeat > 0) {
    if (remainingTime === null || remainingTime > 0) {
      return true;
    }
  }
  return false;
}
